import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  Slider,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  VolumeUp,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { styled } from "@mui/system";
import AirplaneInterior from "../../../src/assets/tracks/AirplaneInterior.mp3";
import { useDispatch } from "react-redux";
import { callApiAction } from "../../store/actions/commonAction";
import { getAudioTrackApi } from "../../apis/audioTrack.api";

const PlayerContainer = styled(Box)({
  width: "13vw",
  background: "#EDF0F7",
  borderRadius: 4,
  color: "white",
  border: "1px solid #2D3B67",
});

const Controls = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 1,
});

const TrackList = styled(Box)(({ isOpen }) => ({
  maxHeight: isOpen ? "150px" : "0px",
  overflowY: "auto",
  marginTop: isOpen ? 8 : 0,
  padding: isOpen ? "8px" : "0px",
  borderRadius: 4,
  background: "#EDF0F7",
  transition: "max-height 0.3s ease, padding 0.3s ease, margin-top 0.3s ease",
}));

const VolumeControl = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 1,
  padding: 1,
});

const CustomSlider = styled(Slider)({
  color: "#2D3B67",
  "& .MuiSlider-thumb": {
    width: 12,
    height: 12,
    backgroundColor: "white",
  },
});

const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(50);
  const [isOpen, setIsOpen] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState(
    tracks && tracks.length > 0 ? tracks[0] : null
  );
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(new Audio(currentTrack?.url));
  const dispatch = useDispatch();

  const fetchAudioTracks = (id) => {
    setLoading(true);
    dispatch(
      callApiAction(
        async () => await getAudioTrackApi({ isAll: true }),
        async (response) => {
          setTracks(response);
          setLoading(false);
        },
        (err) => {
          setLoading(false);
        }
      )
    );
  };

  useEffect(() => {
    fetchAudioTracks();
  }, []);

  useEffect(() => {
    if (tracks.length > 0) {
      setCurrentTrack(tracks[0]);
    }
  }, [tracks]);

  // Effect to handle when track ends
  useEffect(() => {
    const handleTrackEnd = () => {
      if (isPlaying) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    };

    audioRef.current.addEventListener("ended", handleTrackEnd);

    return () => {
      audioRef.current.removeEventListener("ended", handleTrackEnd);
    };
  }, [isPlaying]);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    return () => {
      audioRef.current.pause();
    };
  }, []);

  const changeVolume = (event, newVolume) => {
    setVolume(newVolume);
    audioRef.current.volume = newVolume / 100;
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  const selectTrack = (track) => {
    setCurrentTrack(track);
    setIsOpen(false);
    audioRef.current.src = track.url;
    if (isPlaying) {
      audioRef.current.play();
    }
  };

  return (
    <>
      <PlayerContainer>
        <Typography
          mt={1}
          variant="subtitle1"
          color="#2D3B67"
          align="center"
          gutterBottom
        >
          Music
        </Typography>

        <Controls>
          <IconButton color="primary">
            <SkipPrevious />
          </IconButton>

          <IconButton
            onClick={togglePlay}
            color="primary"
            sx={{ border: "2px solid #2D3B67", borderRadius: "50px" }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          <IconButton color="primary">
            <SkipNext />
          </IconButton>
        </Controls>

        <VolumeControl>
          <VolumeUp sx={{ color: "#2D3B67" }} fontSize="small" />
          <CustomSlider
            value={volume}
            onChange={changeVolume}
            min={0}
            max={100}
            valueLabelDisplay="auto"
            sx={{ width: "100%" }}
          />
        </VolumeControl>

        <Box sx={{ mt: 1 }}>
          <Button
            onClick={toggleDropdown}
            sx={{
              width: "100%",
              textAlign: "left",
              background: "#EDF0F7",
              color: "#2D3B67",
              borderTop: "1px solid #2D3B67",
              borderBottom: "1px solid #2D3B67",
              display: "flex",
              borderRadius: "0",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { background: "#DEE3F1" },
            }}
          >
            <Typography variant="body1"> {currentTrack?.name}</Typography>
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </Button>

          <TrackList isOpen={isOpen}>
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              tracks?.map((track, index) => (
                <Button
                  key={index}
                  onClick={() => selectTrack(track)}
                  sx={{
                    width: "100%",
                    textAlign: "left",
                    color: "#2D3B67",
                    background: "transparent",
                    "&:hover": { background: "#DEE3F1" },
                  }}
                >
                  <Typography variant="body1"> {track?.name}</Typography>
                </Button>
              ))
            )}
          </TrackList>
        </Box>
      </PlayerContainer>
    </>
  );
};

export default AudioPlayer;
